import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDown, Play, Check } from "lucide-react";

const TIKTOK_URL = "https://www.tiktok.com/@schatten_psyche_";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const TiltCover = () => {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 140, damping: 18 });
  const sry = useSpring(ry, { stiffness: 140, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 16);
    rx.set(-py * 16);
  };

  return (
    <div style={{ perspective: 1200 }} className="relative">
      <motion.div
        ref={ref}
        data-testid="hero-book-cover"
        onMouseMove={onMove}
        onMouseLeave={() => {
          rx.set(0);
          ry.set(0);
        }}
        initial={{ opacity: 0, y: 60, rotateZ: 2 }}
        animate={{ opacity: 1, y: 0, rotateZ: 0 }}
        transition={{ duration: 1.2, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d" }}
        className="relative w-64 sm:w-80 lg:w-96 mx-auto cursor-pointer"
      >
        <div className="absolute -inset-6 bg-neon-cyan/10 blur-3xl rounded-full animate-pulse-glow" />
        <div className="absolute -inset-3 bg-neon-magenta/10 blur-2xl rounded-full" />
        <img
          src="/covers/band1.png"
          alt="Schattenpsyche Band 1 — Ein Profiler-Handbuch für den Alltag"
          className="relative w-full drop-shadow-[0_50px_60px_rgba(0,0,0,0.85)]"
          style={{ transform: "translateZ(40px)" }}
        />
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const { scrollY } = useScroll();
  const glowY = useTransform(scrollY, [0, 700], [0, 160]);
  const fade = useTransform(scrollY, [0, 500], [1, 0.2]);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden hero-grid" data-testid="hero-section">
      <motion.div style={{ y: glowY }} className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-[34rem] h-[34rem] bg-neon-cyan/10 rounded-full blur-[140px]" />
        <div className="absolute bottom-0 -right-32 w-[30rem] h-[30rem] bg-neon-magenta/10 rounded-full blur-[140px]" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-32 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center w-full">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="mb-8"
          >
            <span className="stamp text-xs sm:text-sm" data-testid="hero-case-stamp">
              Akte Nº 001 · Dringend
            </span>
          </motion.div>

          <h1
            data-testid="hero-main-title"
            className="font-cinzel font-bold text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight text-white"
          >
            <MaskedLine delay={0.35}>Lies Menschen.</MaskedLine>
            <MaskedLine delay={0.5}>
              Wie ein <span className="italic">Profiler</span>
            </MaskedLine>
            <MaskedLine delay={0.65}>
              im Alltag<span className="text-slate-500">.</span>
            </MaskedLine>
          </h1>

          <motion.div
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-8 max-w-xl"
          >
            <p className="text-base sm:text-lg leading-relaxed text-slate-400">
              <span className="font-typewriter text-paper">SCHATTENPSYCHE</span>, das
              Profiler-Handbuch für den Alltag. In 75 Seiten, ohne Fachchinesisch:
            </p>
            <ul className="mt-5 space-y-2.5 text-sm sm:text-base text-slate-300" data-testid="hero-benefit-list">
              {[
                "Mimik & Körpersprache in Sekunden deuten",
                "Lügen und Manipulation erkennen, bevor sie wirken",
                "Grenzen setzen: ruhig, klar, ohne laut zu werden",
              ].map((b) => (
                <li key={b} className="flex gap-3 items-start">
                  <Check className="w-4 h-4 text-white mt-1 shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2, duration: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <a
              href="#books"
              data-testid="hero-explore-books-cta"
              className="glow-btn !text-base !px-10 !py-5 min-h-[56px]"
            >
              Jetzt Buch sichern · 19,99 €
              <ArrowDown className="w-4 h-4" />
            </a>
            <a
              href={TIKTOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-tiktok-video-cta"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/15 text-slate-300 font-semibold transition-all duration-300 hover:border-neon-magenta/60 hover:text-white hover:shadow-[0_0_25px_rgba(255,0,80,0.25)]"
            >
              <Play className="w-4 h-4 fill-current" />
              @schatten_psyche_
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 0.7 }}
            className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500"
            data-testid="hero-trust-line"
          >
            Sofort-Download (PDF) · 75 Seiten · Band 1 der Trilogie
          </motion.p>
        </motion.div>

        <div className="lg:col-span-5">
          <TiltCover />
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.4em] text-slate-600"
      >
        Scrollen, um zu ermitteln
      </motion.div>
    </section>
  );
};
